import { ChatConversation, MetaAttribution } from '../../../features/chat/hooks/types';
import { metaDisplayName } from '../../modules/leadTrackingService';
import {
    addBrazilPrefix,
    contactPhoneKeyVariants,
    extractNumber,
    formatInstagramHandle,
    isUsableContactName,
    stripBrazilMobileNinthDigit,
} from '../../../utils/phoneUtils';
import { canRenderRemoteAvatarUrl } from '../../../utils/failedImageCache';
import type { DbConversation } from '../types/ChatConversationTypes';

interface DbMetaAttribution {
    ctwa_clid?: string;
    source_id?: string;
    cta_title?: string;
    campaign_id?: string;
    campaign_name?: string;
    adset_id?: string;
    adset_name?: string;
    ad_id?: string;
    ad_name?: string;
    captured_at?: string;
}

/**
 * Mapeia dados de atribuição do banco para o formato de domínio
 */
function pickAttr(raw: Record<string, unknown>, snake: string, camel: string): string | undefined {
    const v = raw[snake] ?? raw[camel];
    return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

function mapMetaAttribution(dbAttr?: DbMetaAttribution | Record<string, unknown> | null): MetaAttribution | undefined {
    if (!dbAttr) return undefined;
    const raw = dbAttr as Record<string, unknown>;

    return {
        ctwaClid: pickAttr(raw, 'ctwa_clid', 'ctwaClid'),
        sourceId: pickAttr(raw, 'source_id', 'sourceId'),
        googleClickId: pickAttr(raw, 'google_click_id', 'googleClickId'),
        keyword: pickAttr(raw, 'keyword', 'keyword'),
        campaignId: pickAttr(raw, 'campaign_id', 'campaignId'),
        campaignName: pickAttr(raw, 'campaign_name', 'campaignName'),
        adsetId: pickAttr(raw, 'adset_id', 'adsetId'),
        adsetName: pickAttr(raw, 'adset_name', 'adsetName'),
        adId: pickAttr(raw, 'ad_id', 'adId'),
        // CTA no ad_name (DB) não pode aparecer como “Anúncio” no painel de origem.
        adName: metaDisplayName(pickAttr(raw, 'ad_name', 'adName'))
            || metaDisplayName(pickAttr(raw, 'headline', 'headline')),
        headline: metaDisplayName(pickAttr(raw, 'headline', 'headline')),
        capturedAt: pickAttr(raw, 'captured_at', 'capturedAt'),
        igSid: pickAttr(raw, 'ig_sid', 'igSid'),
        instagramBusinessAccountId: pickAttr(raw, 'instagram_business_account_id', 'instagramBusinessAccountId'),
        instagramUsername: pickAttr(raw, 'instagram_username', 'instagramUsername'),
        messagingChannel: (pickAttr(raw, 'messaging_channel', 'messagingChannel') as 'whatsapp' | 'instagram' | undefined),
        origin: pickAttr(raw, 'origin', 'origin'),
    };
}

/**
 * Avatar exibível na lista/header — só URL permanente (R2).
 * CDN WhatsApp/IG fica para sync-contact-avatar persistir.
 */
function getValidAvatar(
    profilePicture?: string,
    waAvatar?: string,
): string | undefined {
    if (profilePicture && canRenderRemoteAvatarUrl(profilePicture)) return profilePicture;
    if (waAvatar && canRenderRemoteAvatarUrl(waAvatar)) return waAvatar;
    return undefined;
}


function lookupByPhone<T>(map: Map<string, T>, jid: string): T | undefined {
    for (const key of contactPhoneKeyVariants(jid)) {
        const hit = map.get(key);
        if (hit !== undefined) return hit;
    }
    return map.get(extractNumber(jid));
}

/**
 * Mapeia uma conversa do banco para o formato de domínio
 */
export function mapDbToChatConversation(
    conv: DbConversation,
    contactIdMap: Map<string, string>,
    contactNameMap: Map<string, string>,
    contactAvatarMap: Map<string, string>,
    leadStageMap: Map<string, string>
): ChatConversation {
    const rawPhone = extractNumber(conv.remote_jid);
    const phone = stripBrazilMobileNinthDigit(addBrazilPrefix(rawPhone));
    const savedId = conv.contact_id || lookupByPhone(contactIdMap, conv.remote_jid);
    const savedName = lookupByPhone(contactNameMap, conv.remote_jid);
    const savedAvatar = lookupByPhone(contactAvatarMap, conv.remote_jid);
    const stageName = lookupByPhone(leadStageMap, conv.remote_jid);

    const isInstagramJid = conv.platform === 'instagram' && conv.remote_jid.startsWith('ig:');
    const meta = mapMetaAttribution(conv.meta_attribution);

    const displayName =
        (isUsableContactName(savedName, phone) ? savedName : undefined) ||
        (isUsableContactName(conv.contact_name, phone) ? conv.contact_name : undefined) ||
        // IG: nunca ficar só em "Contato Instagram" se o Graph já gravou o handle.
        (isInstagramJid ? formatInstagramHandle(meta?.instagramUsername) : undefined) ||
        (isInstagramJid ? 'Contato Instagram' : undefined) ||
        phone ||
        conv.remote_jid.split('@')[0];

    // Prioridade: 1) avatar do contato enriquecido, 2) profile_picture_url da conversa, 3) avatar_url legado
    const resolvedAvatar = getValidAvatar(
        savedAvatar || conv.profile_picture_url || undefined,
        conv.avatar_url || undefined,
    );

    return {
        id: conv.remote_jid,
        name: displayName,
        unreadCount: conv.unread_count || 0,
        messages: [],
        lastMessage: conv.last_message || '',
        lastMessageTime: conv.last_message_time
            ? new Date(conv.last_message_time)
            : new Date(conv.created_at || Date.now()),
        avatar: resolvedAvatar,
        assignedTo: conv.assigned_to || undefined,
        tags: conv.tags || [],
        status: (conv.status as 'active' | 'closed') || 'active',
        stageName,
        contactId: savedId,
        instanceName: undefined,
        metaAttribution: meta,
        platform: conv.platform === 'instagram' ? 'instagram' : 'whatsapp',
        connectionId: conv.connection_id || undefined,
    };
}

function canonicalPhoneKey(jid: string): string {
    return stripBrazilMobileNinthDigit(addBrazilPrefix(extractNumber(jid)));
}

/** Instagram usa remote_jid estável; WhatsApp deduplica por telefone canônico. */
export function conversationDedupeKey(conv: Pick<ChatConversation, 'id' | 'connectionId' | 'platform'>): string {
    const contact = conv.id.startsWith('ig:') || conv.platform === 'instagram' ? conv.id : canonicalPhoneKey(conv.id);
    return `${conv.connectionId || ''}:${contact}`;
}

function pickPreferredConversation(a: ChatConversation, b: ChatConversation): ChatConversation {
    const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
    const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
    if (timeA !== timeB) return timeA > timeB ? a : b;

    const aHasCountryCode = extractNumber(a.id).startsWith('55');
    const bHasCountryCode = extractNumber(b.id).startsWith('55');
    if (aHasCountryCode && !bHasCountryCode) return a;
    if (bHasCountryCode && !aHasCountryCode) return b;

    return a;
}

/**
 * Deduplica conversas WhatsApp pelo telefone canônico (com/sem DDI 55).
 * Conversas Instagram (`ig:`) nunca colapsam com WhatsApp nem entre si por dígitos.
 */
export function deduplicateConversations(conversations: ChatConversation[]): ChatConversation[] {
    const map = new Map<string, ChatConversation>();

    conversations.forEach(conv => {
        const key = conversationDedupeKey(conv);
        const existing = map.get(key);
        map.set(key, existing ? pickPreferredConversation(existing, conv) : conv);
    });

    return Array.from(map.values()).sort((a, b) => {
        const dateA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
        const dateB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
        return dateB - dateA;
    });
}
